import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toLocalISODate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  FileText,
  Download,
  Calendar as CalendarIcon,
  Users,
  Info,
  FileSpreadsheet,
  File,
  Clock,
  Sun,
  Moon,
  Umbrella,
  TrendingUp,
  BarChart3,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  PenLine,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthNamesEs = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const monthNamesEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const pad2 = (n) => String(n).padStart(2, '0');

const Reports = () => {
  const { isAdmin, isSupervisor, isInspector, isWorker, isGestor, currentOrganization } = useAuth();
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [myEmployee, setMyEmployee] = useState(null);
  const [summary, setSummary] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [complianceAlert, setComplianceAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [hashInput, setHashInput] = useState('');
  const [hashResult, setHashResult] = useState(null);
  const [loadingHash, setLoadingHash] = useState(false);
  const today = new Date();
  const [sigYear, setSigYear] = useState(today.getFullYear());
  const [sigMonth, setSigMonth] = useState(today.getMonth()); // 0-indexed
  const [signatures, setSignatures] = useState([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [viewSignature, setViewSignature] = useState(null);
  const [exportTemplates, setExportTemplates] = useState([]);
  const [useCustomTemplate, setUseCustomTemplate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [filters, setFilters] = useState({
    start_date: toLocalISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    end_date: toLocalISODate(new Date()),
    employee_id: '',
    format: 'xlsx'
  });

  // Reset data when organization changes
  useEffect(() => {
    setEmployees([]);
    setMyEmployee(null);
    setSummary(null);
    setCompliance(null);
    setTrends(null);
    setComplianceAlert(null);
    setHashResult(null);
    setExportTemplates([]);
    setUseCustomTemplate(false);
    setSelectedTemplateId('');
    setFilters(prev => ({ ...prev, employee_id: '' }));
  }, [currentOrganization?.id]);

  useEffect(() => {
    if (isWorker()) {
      fetchMyEmployee();
    } else {
      fetchEmployees();
    }
    fetchExportTemplates();
  }, [currentOrganization?.id]);

  const fetchExportTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/export-templates`);
      setExportTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching export templates:', error);
    }
  };

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchSummary();
    }
  }, [filters.start_date, filters.end_date, filters.employee_id]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchMyEmployee = async () => {
    try {
      const response = await axios.get(`${API_URL}/employees/me`);
      setMyEmployee(response.data);
      setFilters(prev => ({ ...prev, employee_id: response.data.id }));
    } catch (error) {
      console.error('Error fetching my employee:', error);
    }
  };

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      if (!isWorker() && filters.employee_id && filters.employee_id !== 'all') {
        params.employee_id = filters.employee_id;
      }
      const response = await axios.get(`${API_URL}/reports/summary`, { params });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const fetchCompliance = async () => {
    setLoadingCompliance(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      const response = await axios.get(`${API_URL}/reports/compliance`, { params });
      setCompliance(response.data);
    } catch (error) {
      console.error('Error fetching compliance:', error);
      toast.error(language === 'es' ? 'Error al cargar informe de cumplimiento' : 'Error loading compliance report');
    } finally {
      setLoadingCompliance(false);
    }
  };

  const handleExportCompliance = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      
      const response = await axios.get(`${API_URL}/reports/compliance/export`, {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_cumplimiento_${filters.start_date}_${filters.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'PDF de cumplimiento exportado' : 'Compliance PDF exported');
    } catch (error) {
      console.error('Error exporting compliance:', error);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Error exporting PDF');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    setLoadingTrends(true);
    try {
      const response = await axios.get(`${API_URL}/reports/compliance/trends`, {
        params: { months: 6 }
      });
      setTrends(response.data);
      
      // Also fetch compliance alerts
      fetchComplianceAlert();
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error(language === 'es' ? 'Error al cargar tendencias' : 'Error loading trends');
    } finally {
      setLoadingTrends(false);
    }
  };

  const signaturePeriod = `${sigYear}-${pad2(sigMonth + 1)}`;
  const sigMonthNames = language === 'es' ? monthNamesEs : monthNamesEn;

  const fetchSignatures = async () => {
    setLoadingSignatures(true);
    try {
      const response = await axios.get(`${API_URL}/signatures`, { params: { period: signaturePeriod } });
      setSignatures(response.data);
    } catch (error) {
      console.error('Error fetching signatures:', error);
      toast.error(language === 'es' ? 'Error al cargar firmas' : 'Error loading signatures');
    } finally {
      setLoadingSignatures(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'signatures') {
      fetchSignatures();
    }
  }, [activeTab, sigYear, sigMonth, currentOrganization?.id]);

  const sigGoPrev = () => {
    if (sigMonth === 0) { setSigMonth(11); setSigYear(sigYear - 1); } else { setSigMonth(sigMonth - 1); }
  };
  const sigGoNext = () => {
    if (sigMonth === 11) { setSigMonth(0); setSigYear(sigYear + 1); } else { setSigMonth(sigMonth + 1); }
  };

  const fetchComplianceAlert = async () => {
    try {
      const response = await axios.get(`${API_URL}/reports/compliance/alerts`);
      setComplianceAlert(response.data);
    } catch (error) {
      console.error('Error fetching compliance alert:', error);
    }
  };

  const verifyHash = async () => {
    if (!hashInput.trim() || hashInput.trim().length < 8) {
      toast.error(language === 'es' ? 'El hash debe tener al menos 8 caracteres' : 'Hash must be at least 8 characters');
      return;
    }
    
    setLoadingHash(true);
    setHashResult(null);
    try {
      const response = await axios.get(`${API_URL}/reports/verify-hash`, {
        params: { hash_code: hashInput.trim() }
      });
      setHashResult(response.data);
      if (response.data.verified) {
        toast.success(language === 'es' ? 'Hash verificado correctamente' : 'Hash verified successfully');
      } else {
        toast.error(language === 'es' ? 'Hash no encontrado' : 'Hash not found');
      }
    } catch (error) {
      console.error('Error verifying hash:', error);
      toast.error(language === 'es' ? 'Error al verificar hash' : 'Error verifying hash');
    } finally {
      setLoadingHash(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        format: filters.format
      };
      
      if (!isWorker() && filters.employee_id && filters.employee_id !== 'all') {
        params.employee_id = filters.employee_id;
      }

      const response = await axios.get(`${API_URL}/reports/export`, {
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_${filters.start_date}_${filters.end_date}.${filters.format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportWithTemplate = async () => {
    if (!selectedTemplateId) {
      toast.error(language === 'es' ? 'Selecciona una plantilla' : 'Select a template');
      return;
    }
    setLoading(true);
    try {
      const params = { start_date: filters.start_date, end_date: filters.end_date };
      if (!isWorker() && filters.employee_id && filters.employee_id !== 'all') {
        params.employee_id = filters.employee_id;
      }

      let response;
      try {
        response = await axios.get(`${API_URL}/export-templates/${selectedTemplateId}/generate`, { params, responseType: 'blob' });
      } catch (error) {
        if (error.response?.status === 409) {
          const missing = JSON.parse(await error.response.data.text()).detail.missing_payroll_code;
          const names = missing.map(m => m.name).join(', ');
          const confirmMsg = language === 'es'
            ? `Estos empleados no tienen código de nómina configurado y se omitirán: ${names}. ¿Continuar?`
            : `These employees don't have a payroll code set and will be skipped: ${names}. Continue?`;
          if (!window.confirm(confirmMsg)) {
            setLoading(false);
            return;
          }
          response = await axios.get(`${API_URL}/export-templates/${selectedTemplateId}/generate`, {
            params: { ...params, confirm_incomplete: true },
            responseType: 'blob'
          });
        } else {
          throw error;
        }
      }

      const template = exportTemplates.find(t => t.id === selectedTemplateId);
      const ext = template?.file_format === 'xlsx' ? 'xlsx' : 'csv';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${template?.name || 'export'}_${filters.start_date}_${filters.end_date}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting with template:', error);
      toast.error(language === 'es' ? 'Error al exportar con la plantilla' : 'Error exporting with template');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCalendar = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        format: filters.format  // Now supports pdf, xlsx, csv
      };
      
      if (!isWorker() && filters.employee_id && filters.employee_id !== 'all') {
        params.employee_id = filters.employee_id;
      }

      const response = await axios.get(`${API_URL}/reports/calendar-export`, {
        params,
        responseType: 'blob'
      });

      const ext = filters.format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `calendario_${filters.start_date}_${filters.end_date}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting calendar:', error);
      alert(t('error_occurred'));
    } finally {
      setLoading(false);
    }
  };

  const quickDateRanges = [
    { 
      label: language === 'es' ? 'Este mes' : 'This month',
      getRange: () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          start_date: toLocalISODate(firstDay),
          end_date: toLocalISODate(lastDay)
        };
      }
    },
    {
      label: language === 'es' ? 'Mes anterior' : 'Last month',
      getRange: () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          start_date: toLocalISODate(firstDay),
          end_date: toLocalISODate(lastDay)
        };
      }
    },
    {
      label: language === 'es' ? 'Este año' : 'This year',
      getRange: () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), 0, 1);
        return {
          start_date: toLocalISODate(firstDay),
          end_date: toLocalISODate(today)
        };
      }
    },
    {
      label: language === 'es' ? 'Año anterior' : 'Last year',
      getRange: () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear() - 1, 0, 1);
        const lastDay = new Date(today.getFullYear() - 1, 11, 31);
        return {
          start_date: toLocalISODate(firstDay),
          end_date: toLocalISODate(lastDay)
        };
      }
    }
  ];

  const formatOptions = [
    { value: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { value: 'csv', label: 'CSV (.csv)', icon: FileText, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { value: 'pdf', label: 'PDF (.pdf)', icon: File, color: 'bg-red-100 text-red-700 border-red-300' }
  ];

  const SummaryCard = ({ icon: Icon, label, value, unit, color }) => (
    <div className={`p-4 rounded-lg border ${color}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">
        {value} <span className="text-sm font-normal">{unit}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('reports')}</h1>
        <p className="text-slate-500">
          {isWorker() 
            ? (language === 'es' ? 'Consulta tu resumen de horas y descarga tus informes' : 'View your hours summary and download your reports')
            : (language === 'es' ? 'Resumen de horas y exportación de datos' : 'Hours summary and data export')
          }
        </p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* Quick ranges */}
            <div className="flex flex-wrap gap-2">
              {quickDateRanges.map((range, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const dates = range.getRange();
                    setFilters(prev => ({ ...prev, ...dates }));
                  }}
                >
                  {range.label}
                </Button>
              ))}
            </div>
            
            <div className="flex gap-2 flex-1 min-w-[300px]">
              <div className="flex-1">
                <Label className="text-xs mb-1 block">{t('start_date')}</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  className="h-9"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs mb-1 block">{t('end_date')}</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  className="h-9"
                />
              </div>
            </div>

            {!isWorker() && (
              <div className="min-w-[200px]">
                <Label className="text-xs mb-1 block">{t('employees')}</Label>
                <Select 
                  value={filters.employee_id} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={language === 'es' ? 'Todos' : 'All'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? 'Todos los empleados' : 'All employees'}</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 w-full lg:w-auto">
          <TabsTrigger value="summary" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            {language === 'es' ? 'Resumen' : 'Summary'}
          </TabsTrigger>
          <TabsTrigger value="export" className="gap-2">
            <FileText className="w-4 h-4" />
            {language === 'es' ? 'Exportar Fichajes' : 'Export Records'}
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <CalendarIcon className="w-4 h-4" />
            {language === 'es' ? 'Exportar Calendario' : 'Export Calendar'}
          </TabsTrigger>
          {(isAdmin() || isSupervisor()) && (
            <TabsTrigger value="compliance" className="gap-2" onClick={() => !compliance && fetchCompliance()}>
              <ShieldCheck className="w-4 h-4" />
              {language === 'es' ? 'Cumplimiento' : 'Compliance'}
            </TabsTrigger>
          )}
          {(isAdmin() || isSupervisor()) && (
            <TabsTrigger value="trends" className="gap-2" onClick={() => !trends && fetchTrends()}>
              <TrendingUp className="w-4 h-4" />
              {language === 'es' ? 'Tendencias' : 'Trends'}
            </TabsTrigger>
          )}
          {(isAdmin() || isSupervisor() || isInspector()) && (
            <TabsTrigger value="signatures" className="gap-2">
              <PenLine className="w-4 h-4" />
              {language === 'es' ? 'Firmas' : 'Signatures'}
            </TabsTrigger>
          )}
          <TabsTrigger value="verify" className="gap-2">
            <ShieldCheck className="w-4 h-4" />
            {language === 'es' ? 'Verificar Hash' : 'Verify Hash'}
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-6">
          {loadingSummary ? (
            <div className="flex items-center justify-center h-40">
              <div className="spinner" />
            </div>
          ) : summary ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <SummaryCard
                  icon={Clock}
                  label={language === 'es' ? 'Horas Trabajadas' : 'Worked Hours'}
                  value={summary.totals?.worked_hours_formatted || '0:00'}
                  unit=""
                  color="bg-indigo-50 border-indigo-200 text-indigo-900"
                />
                <SummaryCard
                  icon={TrendingUp}
                  label={language === 'es' ? 'Horas Extra' : 'Extra Hours'}
                  value={summary.totals?.extra_hours_formatted || '0:00'}
                  unit=""
                  color="bg-amber-50 border-amber-200 text-amber-900"
                />
                <SummaryCard
                  icon={Moon}
                  label={language === 'es' ? 'Horas Nocturnas' : 'Night Hours'}
                  value={summary.totals?.night_hours_formatted || '0:00'}
                  unit=""
                  color="bg-slate-100 border-slate-300 text-slate-900"
                />
                {/* Festivos - días y horas */}
                <div className="p-4 rounded-lg border bg-red-50 border-red-200 text-red-900">
                  <div className="flex items-center gap-2 mb-1">
                    <Sun className="w-4 h-4" />
                    <span className="text-sm font-medium">{language === 'es' ? 'Festivos Trabajados' : 'Holidays Worked'}</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {summary.totals?.holidays_worked || 0} <span className="text-sm font-normal">{language === 'es' ? 'días' : 'days'}</span>
                  </div>
                  <div className="text-sm mt-1">
                    {summary.totals?.holiday_hours_formatted || '0:00'}
                  </div>
                </div>
                {/* Domingos - días y horas */}
                <div className="p-4 rounded-lg border bg-purple-50 border-purple-200 text-purple-900">
                  <div className="flex items-center gap-2 mb-1">
                    <Umbrella className="w-4 h-4" />
                    <span className="text-sm font-medium">{language === 'es' ? 'Domingos Trabajados' : 'Sundays Worked'}</span>
                  </div>
                  <div className="text-2xl font-bold">
                    {summary.totals?.sundays_worked || 0} <span className="text-sm font-normal">{language === 'es' ? 'días' : 'days'}</span>
                  </div>
                  <div className="text-sm mt-1">
                    {summary.totals?.sunday_hours_formatted || '0:00'}
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-600">{language === 'es' ? 'Días trabajados' : 'Days worked'}</div>
                  <div className="text-xl font-semibold">{summary.totals?.days_worked || 0}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-600">{language === 'es' ? 'Horas previstas' : 'Expected hours'}</div>
                  <div className="text-xl font-semibold">{summary.totals?.expected_hours_formatted || '0:00'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-600">{language === 'es' ? 'Horas festivas' : 'Holiday hours'}</div>
                  <div className="text-xl font-semibold">{summary.totals?.holiday_hours_formatted || '0:00'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm text-slate-600">{language === 'es' ? 'Horas ausencias' : 'Absence hours'}</div>
                  <div className="text-xl font-semibold">{summary.totals?.absence_hours_formatted || '0:00'}</div>
                </div>
              </div>

              {/* Employee breakdown - only for admin/supervisor */}
              {!isWorker() && summary.employees && summary.employees.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Manrope']">
                      {language === 'es' ? 'Desglose por empleado' : 'Breakdown by employee'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('name')}</TableHead>
                          <TableHead>{t('department')}</TableHead>
                          <TableHead className="text-right">{language === 'es' ? 'Horas Trabajadas' : 'Worked'}</TableHead>
                          <TableHead className="text-right">{language === 'es' ? 'Extras' : 'Extra'}</TableHead>
                          <TableHead className="text-right">{language === 'es' ? 'Festivos' : 'Holidays'}</TableHead>
                          <TableHead className="text-right">{language === 'es' ? 'Domingos' : 'Sundays'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.employees.map((emp) => (
                          <TableRow key={emp.employee_id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell className="text-slate-600">{emp.department || '-'}</TableCell>
                            <TableCell className="text-right">{emp.worked_hours_formatted || '0:00'}</TableCell>
                            <TableCell className="text-right text-amber-600">{emp.extra_hours_formatted || '0:00'}</TableCell>
                            <TableCell className="text-right text-red-600">{emp.holiday_hours_formatted || '0:00'}</TableCell>
                            <TableCell className="text-right text-purple-600">{emp.sunday_hours_formatted || '0:00'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Incidents section - Always show */}
              <Card className="border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope'] text-amber-800">
                    {language === 'es' ? 'Incidencias del Período' : 'Period Incidents'}
                    {summary.incidents && summary.incidents.length > 0 && (
                      <span className="ml-2 text-sm font-normal">({summary.incidents.length})</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {summary.incidents && summary.incidents.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-amber-100/50">
                          <TableHead>{language === 'es' ? 'Empleado' : 'Employee'}</TableHead>
                          <TableHead>{language === 'es' ? 'Tipo' : 'Type'}</TableHead>
                          <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                          <TableHead>{language === 'es' ? 'Motivo' : 'Reason'}</TableHead>
                          <TableHead>{language === 'es' ? 'Corrección propuesta' : 'Proposed correction'}</TableHead>
                          <TableHead>{language === 'es' ? 'Creada por' : 'Created by'}</TableHead>
                          <TableHead>{language === 'es' ? 'Resuelta por' : 'Resolved by'}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summary.incidents.map((inc) => (
                          <TableRow key={inc.id}>
                            <TableCell className="font-medium">{inc.employee_name}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                inc.incident_type === 'forgotten_clock' ? 'bg-blue-100 text-blue-800' :
                                inc.incident_type === 'time_correction' ? 'bg-purple-100 text-purple-800' :
                                inc.incident_type === 'wrong_type' ? 'bg-orange-100 text-orange-800' :
                                'bg-slate-100 text-slate-800'
                              }`}>
                                {language === 'es' ? {
                                  'forgotten_clock': 'Olvido fichaje',
                                  'time_correction': 'Corrección hora',
                                  'wrong_type': 'Tipo incorrecto',
                                  'duplicate': 'Duplicado',
                                  'other': 'Otro'
                                }[inc.incident_type] || inc.incident_type : inc.incident_type}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                inc.status === 'approved' ? 'bg-green-100 text-green-800' :
                                inc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {language === 'es' ? {
                                  'pending': 'Pendiente',
                                  'approved': 'Aprobada',
                                  'rejected': 'Rechazada'
                                }[inc.status] || inc.status : inc.status}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate" title={inc.reason}>
                              {inc.reason || '-'}
                            </TableCell>
                            <TableCell className="text-xs text-slate-600">
                              {inc.proposed_timestamp ? new Date(inc.proposed_timestamp).toLocaleString(language === 'es' ? 'es-ES' : 'en-US') : '-'}
                            </TableCell>
                            <TableCell className="text-slate-600">{inc.created_by_name || inc.created_by || '-'}</TableCell>
                            <TableCell className="text-slate-600">{inc.resolved_by_name || inc.resolved_by || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="p-4 text-center text-slate-500">
                      {language === 'es' ? 'No hay incidencias en este período' : 'No incidents in this period'}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-8 text-slate-500">
              {language === 'es' ? 'No hay datos para el período seleccionado' : 'No data for selected period'}
            </div>
          )}
        </TabsContent>

        {/* Export Records Tab */}
        <TabsContent value="export">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Exportar Fichajes' : 'Export Time Records'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {isWorker() && myEmployee && (
                <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <Info className="w-5 h-5 text-indigo-600" />
                  <p className="text-sm text-indigo-700">
                    {language === 'es' 
                      ? `Exportando datos de: ${myEmployee.name}`
                      : `Exporting data for: ${myEmployee.name}`}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Label>{language === 'es' ? 'Formato de exportación' : 'Export format'}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {formatOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = filters.format === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, format: option.value }))}
                        className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          isSelected 
                            ? `${option.color} border-current` 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  {language === 'es'
                    ? 'Incluye: registros de entrada/salida, descansos, horas trabajadas, extras, nocturnas y festivas.'
                    : 'Includes: clock in/out records, breaks, worked hours, extra, night, and holiday hours.'}
                </p>
              </div>

              {(isAdmin() || isGestor()) && (
                <div className="space-y-3 p-4 border border-slate-200 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomTemplate}
                      onChange={(e) => setUseCustomTemplate(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-slate-700">
                      {language === 'es' ? 'Usar plantilla personalizada (para nómina)' : 'Use custom template (for payroll)'}
                    </span>
                  </label>
                  {useCustomTemplate && (
                    exportTemplates.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        {language === 'es'
                          ? 'No tienes plantillas configuradas. Créalas en "Plantillas de Exportación".'
                          : 'No templates configured yet. Create them in "Export Templates".'}
                      </p>
                    ) : (
                      <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                        <SelectTrigger>
                          <SelectValue placeholder={language === 'es' ? 'Selecciona una plantilla' : 'Select a template'} />
                        </SelectTrigger>
                        <SelectContent>
                          {exportTemplates.map((tpl) => (
                            <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )
                  )}
                </div>
              )}

              <Button
                onClick={useCustomTemplate ? handleExportWithTemplate : handleExport}
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    {language === 'es' ? 'Generando...' : 'Generating...'}
                  </span>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    {language === 'es' ? 'Exportar Fichajes' : 'Export Records'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Calendar Tab */}
        <TabsContent value="calendar">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Exportar Calendario Programado' : 'Export Scheduled Calendar'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {isWorker() && myEmployee && (
                <div className="flex items-center gap-3 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                  <Info className="w-5 h-5 text-indigo-600" />
                  <p className="text-sm text-indigo-700">
                    {language === 'es' 
                      ? `Exportando calendario de: ${myEmployee.name}`
                      : `Exporting calendar for: ${myEmployee.name}`}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Label>{language === 'es' ? 'Formato de exportación' : 'Export format'}</Label>
                <div className="grid grid-cols-2 gap-3">
                  {formatOptions.filter(o => o.value !== 'pdf').map((option) => {
                    const Icon = option.icon;
                    const isSelected = filters.format === option.value || (filters.format === 'pdf' && option.value === 'xlsx');
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, format: option.value }))}
                        className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                          isSelected 
                            ? `${option.color} border-current` 
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="w-6 h-6" />
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">
                  {language === 'es' ? 'El calendario incluye:' : 'The calendar includes:'}
                </p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• {language === 'es' ? 'Turnos asignados por día' : 'Assigned shifts by day'}</li>
                  <li>• {language === 'es' ? 'Horarios de cada turno' : 'Schedule for each shift'}</li>
                  <li>• {language === 'es' ? 'Vacaciones aprobadas' : 'Approved vacations'}</li>
                  <li>• {language === 'es' ? 'Ausencias (bajas médicas, personales, etc.)' : 'Absences (sick leave, personal, etc.)'}</li>
                  <li>• {language === 'es' ? 'Festivos' : 'Holidays'}</li>
                </ul>
              </div>

              <Button 
                onClick={handleExportCalendar}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    {language === 'es' ? 'Generando...' : 'Generating...'}
                  </span>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    {language === 'es' ? 'Exportar Calendario' : 'Export Calendar'}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Informe de Cumplimiento Normativo' : 'Regulatory Compliance Report'}
              </CardTitle>
              <p className="text-sm text-slate-500">
                {language === 'es' 
                  ? 'Verificación de cumplimiento con RD 8/2019 para inspección de trabajo'
                  : 'Compliance verification with RD 8/2019 for labor inspection'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingCompliance ? (
                <div className="flex items-center justify-center h-40">
                  <div className="spinner" />
                </div>
              ) : compliance ? (
                <>
                  {/* Compliance Score */}
                  <div className="flex items-center justify-between p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {language === 'es' ? 'Puntuación de Cumplimiento' : 'Compliance Score'}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {language === 'es' ? 'Basado en los criterios de inspección de trabajo' : 'Based on labor inspection criteria'}
                      </p>
                    </div>
                    <div className={`text-5xl font-bold ${
                      compliance.summary.compliance_score >= 90 ? 'text-green-600' :
                      compliance.summary.compliance_score >= 70 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {compliance.summary.compliance_score}%
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-slate-900">{compliance.summary.total_employees}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Empleados' : 'Employees'}</div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-slate-900">{compliance.summary.total_records}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Registros' : 'Records'}</div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-slate-900">{compliance.summary.total_incidents}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Incidencias' : 'Incidents'}</div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-slate-900">{compliance.summary.total_corrections}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Correcciones' : 'Corrections'}</div>
                    </div>
                  </div>

                  {/* Compliance Items */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900">
                      {language === 'es' ? 'Verificación de Requisitos' : 'Requirements Verification'}
                    </h4>
                    {compliance.compliance_items.map((item, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-lg">
                        <div className="flex-shrink-0 mt-0.5">
                          {item.status.includes('✅') ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : item.status.includes('⚠️') ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">{item.rule}</span>
                            <Badge variant={item.status.includes('✅') ? 'default' : item.status.includes('⚠️') ? 'secondary' : 'destructive'}>
                              {item.status.replace('✅', '').replace('⚠️', '').replace('❌', '').trim()}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                          <p className="text-xs text-slate-500 mt-1">{item.details}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Warnings */}
                  {compliance.warnings && compliance.warnings.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {language === 'es' ? 'Advertencias' : 'Warnings'}
                      </h4>
                      {compliance.warnings.map((warning, index) => (
                        <div key={index} className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                          {warning.message}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Digital Signature */}
                  <div className="p-4 bg-slate-100 rounded-lg border border-slate-300">
                    <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      {language === 'es' ? 'Firma Digital del Informe' : 'Report Digital Signature'}
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-slate-600">{language === 'es' ? 'ID Documento:' : 'Document ID:'}</div>
                      <div className="font-mono text-slate-900">{compliance.signature.signature_id}</div>
                      <div className="text-slate-600">Hash:</div>
                      <div className="font-mono text-slate-900">{compliance.signature.hash}</div>
                      <div className="text-slate-600">{language === 'es' ? 'Firma:' : 'Signature:'}</div>
                      <div className="font-mono text-slate-900 break-all">{compliance.signature.signature}</div>
                      <div className="text-slate-600">{language === 'es' ? 'Generado:' : 'Generated:'}</div>
                      <div className="text-slate-900">{compliance.generated_at?.slice(0, 19).replace('T', ' ')} UTC</div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      onClick={fetchCompliance}
                      variant="outline"
                      className="flex-1"
                    >
                      {language === 'es' ? 'Actualizar Informe' : 'Refresh Report'}
                    </Button>
                    <Button 
                      onClick={handleExportCompliance}
                      disabled={loading}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="spinner" />
                          {language === 'es' ? 'Exportando...' : 'Exporting...'}
                        </span>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          {language === 'es' ? 'Exportar PDF para Inspección' : 'Export PDF for Inspection'}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <ShieldCheck className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">
                    {language === 'es' 
                      ? 'Haz clic en "Generar Informe" para verificar el cumplimiento normativo'
                      : 'Click "Generate Report" to verify regulatory compliance'}
                  </p>
                  <Button onClick={fetchCompliance} disabled={loadingCompliance}>
                    {loadingCompliance ? (
                      <span className="flex items-center gap-2">
                        <span className="spinner" />
                        {language === 'es' ? 'Generando...' : 'Generating...'}
                      </span>
                    ) : (
                      language === 'es' ? 'Generar Informe de Cumplimiento' : 'Generate Compliance Report'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
                {language === 'es' ? 'Tendencias de Cumplimiento' : 'Compliance Trends'}
              </CardTitle>
              <p className="text-sm text-slate-500">
                {language === 'es' 
                  ? 'Análisis mensual de incidencias y patrones de cumplimiento (últimos 6 meses)'
                  : 'Monthly analysis of incidents and compliance patterns (last 6 months)'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingTrends ? (
                <div className="flex items-center justify-center h-40">
                  <div className="spinner" />
                </div>
              ) : trends ? (
                <>
                  {/* Compliance Alert Banner */}
                  {complianceAlert && complianceAlert.alert_enabled && complianceAlert.is_below_threshold && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="compliance-alert-banner">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-800">
                            {language === 'es' ? 'Alerta de Cumplimiento' : 'Compliance Alert'}
                          </h4>
                          <p className="text-sm text-red-700 mt-1">
                            {complianceAlert.message}
                          </p>
                          {complianceAlert.recommendations && complianceAlert.recommendations.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {complianceAlert.recommendations.map((rec, idx) => (
                                <li key={idx} className="text-sm text-red-600 flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="mt-3 flex items-center gap-4 text-xs text-red-600">
                            <span>{language === 'es' ? 'Período:' : 'Period:'} {complianceAlert.period?.start} - {complianceAlert.period?.end}</span>
                            <span>{language === 'es' ? 'Umbral:' : 'Threshold:'} {complianceAlert.threshold}%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 text-center">
                      <div className="text-3xl font-bold text-indigo-700">{trends.summary.total_records}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Fichajes Totales' : 'Total Records'}</div>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 text-center">
                      <div className="text-3xl font-bold text-amber-700">{trends.summary.total_incidents}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Incidencias' : 'Incidents'}</div>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 text-center">
                      <div className="text-3xl font-bold text-green-700">{trends.summary.total_corrections}</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Correcciones' : 'Corrections'}</div>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 text-center">
                      <div className="text-3xl font-bold text-blue-700">{trends.summary.average_compliance}%</div>
                      <div className="text-sm text-slate-600">{language === 'es' ? 'Cumplimiento Medio' : 'Avg Compliance'}</div>
                    </div>
                  </div>

                  {/* Monthly Trends Chart (Table) */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {language === 'es' ? 'Evolución Mensual' : 'Monthly Evolution'}
                    </h4>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{language === 'es' ? 'Mes' : 'Month'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Fichajes' : 'Records'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Incidencias' : 'Incidents'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Aprobadas' : 'Approved'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Rechazadas' : 'Rejected'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Pendientes' : 'Pending'}</TableHead>
                            <TableHead className="text-center">{language === 'es' ? 'Cumplimiento' : 'Compliance'}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trends.trends.map((month, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{month.month_name}</TableCell>
                              <TableCell className="text-center">{month.records}</TableCell>
                              <TableCell className="text-center">
                                <Badge variant={month.incidents > 5 ? "destructive" : month.incidents > 0 ? "secondary" : "default"}>
                                  {month.incidents}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center text-green-600">{month.approved}</TableCell>
                              <TableCell className="text-center text-red-600">{month.rejected}</TableCell>
                              <TableCell className="text-center text-amber-600">{month.pending}</TableCell>
                              <TableCell className="text-center">
                                <span className={`font-semibold ${
                                  month.compliance_score >= 90 ? 'text-green-600' :
                                  month.compliance_score >= 70 ? 'text-amber-600' : 'text-red-600'
                                }`}>
                                  {month.compliance_score}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Incident Types Distribution */}
                  {trends.incident_types && trends.incident_types.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {language === 'es' ? 'Distribución por Tipo de Incidencia' : 'Distribution by Incident Type'}
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {trends.incident_types.map((type, index) => (
                          <div key={index} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="text-lg font-bold text-slate-900">{type.count}</div>
                            <div className="text-sm text-slate-600">{type.type}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={fetchTrends}
                    variant="outline"
                    className="w-full"
                  >
                    {language === 'es' ? 'Actualizar Tendencias' : 'Refresh Trends'}
                  </Button>
                </>
              ) : (
                <div className="text-center py-10">
                  <TrendingUp className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">
                    {language === 'es' 
                      ? 'Haz clic en "Ver Tendencias" para analizar los patrones de cumplimiento'
                      : 'Click "View Trends" to analyze compliance patterns'}
                  </p>
                  <Button onClick={fetchTrends} disabled={loadingTrends}>
                    {loadingTrends ? (
                      <span className="flex items-center gap-2">
                        <span className="spinner" />
                        {language === 'es' ? 'Cargando...' : 'Loading...'}
                      </span>
                    ) : (
                      language === 'es' ? 'Ver Tendencias de Cumplimiento' : 'View Compliance Trends'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Signatures Tab */}
        <TabsContent value="signatures">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="font-['Manrope'] flex items-center gap-2">
                    <PenLine className="w-5 h-5 text-indigo-600" />
                    {language === 'es' ? 'Conformidad de los Trabajadores' : 'Employee Sign-off'}
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {language === 'es'
                      ? 'Estado de la firma de conformidad del resumen mensual, por empleado'
                      : 'Sign-off status of the monthly hours summary, by employee'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={sigGoPrev}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium text-slate-900 min-w-[140px] text-center">
                    {sigMonthNames[sigMonth]} {sigYear}
                  </span>
                  <Button variant="outline" size="icon" onClick={sigGoNext}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingSignatures ? (
                <div className="flex items-center justify-center h-40">
                  <div className="spinner" />
                </div>
              ) : (
                <>
                  {(() => {
                    const activeEmployees = employees.filter((e) => e.is_active !== false);
                    const rows = activeEmployees
                      .map((emp) => ({
                        employee: emp,
                        signature: signatures.find((s) => s.employee_id === emp.id) || null,
                      }))
                      .sort((a, b) => (a.employee.name || '').localeCompare(b.employee.name || ''));
                    const signedCount = rows.filter((r) => r.signature && r.signature.up_to_date).length;
                    const outdatedCount = rows.filter((r) => r.signature && !r.signature.up_to_date).length;
                    const pendingCount = rows.filter((r) => !r.signature).length;

                    return (
                      <>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-4 rounded-lg border bg-emerald-50 border-emerald-200 text-center">
                            <div className="text-2xl font-bold text-emerald-700">{signedCount}</div>
                            <div className="text-sm text-emerald-700">{language === 'es' ? 'Firmado' : 'Signed'}</div>
                          </div>
                          <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 text-center">
                            <div className="text-2xl font-bold text-amber-700">{outdatedCount}</div>
                            <div className="text-sm text-amber-700">{language === 'es' ? 'Desactualizado' : 'Outdated'}</div>
                          </div>
                          <div className="p-4 rounded-lg border bg-slate-100 border-slate-300 text-center">
                            <div className="text-2xl font-bold text-slate-700">{pendingCount}</div>
                            <div className="text-sm text-slate-600">{language === 'es' ? 'Sin firmar' : 'Not signed'}</div>
                          </div>
                        </div>

                        {rows.length === 0 ? (
                          <div className="text-center py-8 text-slate-500">
                            {language === 'es' ? 'No hay empleados activos' : 'No active employees'}
                          </div>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('department')}</TableHead>
                                <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                                <TableHead>{language === 'es' ? 'Fecha de firma' : 'Signed on'}</TableHead>
                                <TableHead>{language === 'es' ? 'Firma' : 'Signature'}</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rows.map(({ employee, signature }) => (
                                <TableRow key={employee.id}>
                                  <TableCell className="font-medium">{employee.name}</TableCell>
                                  <TableCell className="text-slate-600">{employee.department || '-'}</TableCell>
                                  <TableCell>
                                    {!signature ? (
                                      <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                                        <XCircle className="w-3 h-3 mr-1" />
                                        {language === 'es' ? 'Sin firmar' : 'Not signed'}
                                      </Badge>
                                    ) : signature.up_to_date ? (
                                      <Badge className="bg-emerald-100 text-emerald-800">
                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                        {language === 'es' ? 'Firmado' : 'Signed'}
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-amber-100 text-amber-800">
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        {language === 'es' ? 'Desactualizado' : 'Outdated'}
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-slate-600 text-sm">
                                    {signature?.signed_at
                                      ? new Date(signature.signed_at).toLocaleString(language === 'es' ? 'es-ES' : 'en-US')
                                      : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {signature?.signature_image ? (
                                      <button
                                        type="button"
                                        onClick={() => setViewSignature({ employee, signature })}
                                        className="p-1 bg-white border border-slate-200 rounded hover:border-indigo-300 transition-colors"
                                      >
                                        <img src={signature.signature_image} alt="firma" className="h-8" />
                                      </button>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </>
                    );
                  })()}

                  <Button onClick={fetchSignatures} variant="outline" className="w-full">
                    {language === 'es' ? 'Actualizar' : 'Refresh'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hash Verification Tab */}
        <TabsContent value="verify">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-teal-600" />
                {language === 'es' ? 'Verificación de Hash de Fichajes' : 'Time Record Hash Verification'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <p className="text-sm text-teal-800">
                  {language === 'es' 
                    ? 'Introduce el hash SHA-256 de un fichaje para verificar su autenticidad. El hash garantiza que el registro no ha sido manipulado desde su creación.'
                    : 'Enter the SHA-256 hash of a time record to verify its authenticity. The hash ensures the record has not been tampered with since creation.'}
                </p>
              </div>
              
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label>{language === 'es' ? 'Hash del fichaje' : 'Time record hash'}</Label>
                  <Input
                    placeholder={language === 'es' ? 'Ej: A1B2C3D4E5F6G7H8' : 'e.g. A1B2C3D4E5F6G7H8'}
                    value={hashInput}
                    onChange={(e) => setHashInput(e.target.value.toUpperCase())}
                    className="font-mono"
                    maxLength={16}
                    data-testid="hash-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'es' ? 'Mínimo 8 caracteres. Disponible en los informes exportados.' : 'Minimum 8 characters. Available in exported reports.'}
                  </p>
                </div>
                <Button 
                  onClick={verifyHash} 
                  disabled={loadingHash || hashInput.length < 8}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="verify-hash-btn"
                >
                  {loadingHash ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {language === 'es' ? 'Verificando...' : 'Verifying...'}
                    </span>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      {language === 'es' ? 'Verificar' : 'Verify'}
                    </>
                  )}
                </Button>
              </div>
              
              {hashResult && (
                <div className="mt-6 space-y-4">
                  {hashResult.verified ? (
                    <>
                      <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">{hashResult.message}</span>
                      </div>
                      
                      {hashResult.records?.map((record, idx) => (
                        <Card key={idx} className="border-green-200">
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <Label className="text-slate-500">{language === 'es' ? 'Empleado' : 'Employee'}</Label>
                                <p className="font-medium">{record.employee_name}</p>
                                <p className="text-sm text-slate-500">{record.employee_email}</p>
                              </div>
                              <div>
                                <Label className="text-slate-500">{language === 'es' ? 'Tipo de fichaje' : 'Record type'}</Label>
                                <p className="font-medium">{record.record_type}</p>
                              </div>
                              <div>
                                <Label className="text-slate-500">{language === 'es' ? 'Fecha y hora' : 'Date and time'}</Label>
                                <p className="font-medium">{record.timestamp_formatted}</p>
                              </div>
                              <div>
                                <Label className="text-slate-500">Hash</Label>
                                <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{record.record_hash}</p>
                              </div>
                              <div>
                                <Label className="text-slate-500">{language === 'es' ? 'Estado de integridad' : 'Integrity status'}</Label>
                                <Badge className={record.hash_valid ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                                  {record.integrity_status}
                                </Badge>
                              </div>
                              <div>
                                <Label className="text-slate-500">{language === 'es' ? 'Registro bloqueado' : 'Record locked'}</Label>
                                <Badge className={record.is_locked ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'}>
                                  {record.is_locked ? (language === 'es' ? 'Sí (inmutable)' : 'Yes (immutable)') : 'No'}
                                </Badge>
                              </div>
                              {record.is_correction && (
                                <div className="col-span-full">
                                  <Label className="text-slate-500">{language === 'es' ? 'Corrección de incidencia' : 'Incident correction'}</Label>
                                  <p className="text-sm text-amber-700">
                                    {language === 'es' ? 'Este registro es una corrección desde incidencia: ' : 'This record is a correction from incident: '}
                                    <span className="font-mono">{record.correction_from_incident?.slice(0, 8)}...</span>
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                      <XCircle className="w-5 h-5" />
                      <span className="font-medium">{hashResult.message}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="border-t pt-4 mt-6">
                <h4 className="font-medium mb-2">{language === 'es' ? '¿Dónde encontrar el hash?' : 'Where to find the hash?'}</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• {language === 'es' ? 'En los informes exportados (Excel/CSV/PDF) - columnas "Hash Entrada" y "Hash Salida"' : 'In exported reports (Excel/CSV/PDF) - "Hash Entrada" and "Hash Salida" columns'}</li>
                  <li>• {language === 'es' ? 'El hash es único para cada fichaje y garantiza su inalterabilidad' : 'The hash is unique for each time record and guarantees its immutability'}</li>
                  <li>• {language === 'es' ? 'Formato: 16 caracteres hexadecimales (A-F, 0-9)' : 'Format: 16 hexadecimal characters (A-F, 0-9)'}</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Signature detail modal */}
      {viewSignature && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setViewSignature(null)}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-1">
              {viewSignature.employee.name}
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {language === 'es' ? 'Firmado el ' : 'Signed on '}
              {new Date(viewSignature.signature.signed_at).toLocaleString(language === 'es' ? 'es-ES' : 'en-US')}
            </p>
            <div className="p-3 bg-white border border-slate-200 rounded-lg">
              <img src={viewSignature.signature.signature_image} alt="firma" className="w-full" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm mt-4">
              <div className="text-slate-500">{language === 'es' ? 'Horas trabajadas' : 'Worked hours'}</div>
              <div className="text-slate-900 text-right">{viewSignature.signature.worked_hours?.toFixed?.(2) ?? viewSignature.signature.worked_hours}</div>
              <div className="text-slate-500">{language === 'es' ? 'Horas previstas' : 'Expected hours'}</div>
              <div className="text-slate-900 text-right">{viewSignature.signature.expected_hours?.toFixed?.(2) ?? viewSignature.signature.expected_hours}</div>
              <div className="text-slate-500">IP</div>
              <div className="text-slate-900 text-right font-mono">{viewSignature.signature.ip_address || '-'}</div>
            </div>
            <div className="flex justify-end mt-6">
              <Button variant="outline" onClick={() => setViewSignature(null)}>
                {language === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
